import { makeAutoObservable, runInAction } from 'mobx';

import { Country } from './types/api';
import { logError } from '../../helpers/functions/api';
import { getCountriesConfig } from './api';
import { BaseRootStore } from '../rootStore/baseRootStore';

export default class CountriesStore<IRootStore extends BaseRootStore> {
  rootStore: IRootStore;

  countriesList: Country[] = [];

  loading = false;

  error: Error | null = null;

  constructor(rootStore: IRootStore) {
    makeAutoObservable(this);
    this.rootStore = rootStore;
  }

  get loaded() {
    return this.countriesList.length !== 0;
  }

  get countriesAlpha2List() {
    return this.countriesList.map((country) => country.alpha2);
  }

  get countriesTrialAvailableAlpha3List() {
    return this.countriesList
      .filter((country) => country.trialAvailable)
      .map((country) => country.alpha3);
  }

  get alpha2ToCountryMap() {
    return this.countriesList.reduce(
      (acc, country) => ({
        ...acc,
        [country.alpha2]: country,
      }),
      {} as Record<string, Country>,
    );
  }

  get alpha3ToCountryMap() {
    return this.countriesList.reduce(
      (acc, country) => ({
        ...acc,
        [country.alpha3]: country,
      }),
      {} as Record<string, Country>,
    );
  }

  reset() {
    runInAction(() => {
      this.countriesList = [];
      this.loading = false;
      this.error = null;
    });
  }

  async fetchCountriesList() {
    if (this.loaded || this.loading) {
      return;
    }

    this.loading = true;
    this.error = null;

    try {
      const { data } = await getCountriesConfig();

      if (!data?.getManageConfig) {
        throw new Error('[CountriesStore][fetchCountriesList] Data is empty.');
      }

      runInAction(() => {
        const countriesList = JSON.parse(
          data.getManageConfig ?? '',
        ) as Country[];

        this.countriesList = countriesList;
      });
    } catch (error) {
      const typedError = error as Error;
      runInAction(() => {
        this.error = typedError;
      });
      logError(typedError);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }
}
